import { useState } from 'react';
import { agreementSigned, missingAgreementRequirements, partySigned, unsigned, withAdditionalServices, withAgreementDemo, withAgreementFee, withAgreementLanguage, type AgreementLanguage, type V2AdditionalService, type V2Agreement } from '../../domain/agreement';
import type { V2Inspection } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import { labelWithTag } from '../../i18n/labels';
import { Banner, Button, ConfirmSheet, RowInput, SwitchRow } from '../../ui/components';
import { formatDate } from '../../ui/hooks';
import { AgreementLanguageRow } from './AgreementLanguageRow';
import { AgreementSignSheet } from './AgreementSignSheet';
import { AgreementViewSheet } from './AgreementViewSheet';
import { RemoteSignSheet } from './RemoteSignSheet';
import { useStore } from '../../state/createStore';
import { remoteSignStore } from '../../state/remoteSign';
import { agreementPdfFailed, agreementPdfStore, retryAgreementPdf } from '../../state/agreementPdfStore';

// Inspection Agreement card (Owner task book 2026-09-04 Round 2 §C): the FIXED approved document
// with only the variable fields on the card — the manually recorded fee OR Demo, the two signature
// states — and the two actions 查看协议 / 签署协议. No scope selector, no terms toggle, no payment.
// The same card serves the New Inspection form (draft held in state, `embedded` in step 4 of the
// Owner-selected four-step design, Figma 191:7) and Inspection Details (persisted, its own card).
// Owner-selected design 2026-09-14: grey rows (fee typed in place · Demo switch), the AI block in
// AI purple with its switch, two signature rows, the missing line, 查看协议 · 签署协议 side by side.
export type AgreementSubject = Pick<V2Inspection, 'property' | 'client' | 'inspector' | 'inspectionAt'>;

const REQUIREMENT_KEYS: Record<ReturnType<typeof missingAgreementRequirements>[number], MessageKey> = {
  fee: 'agreement.req.fee',
  additionalServices: 'agreement.req.additionalServices',
  clientName: 'agreement.req.clientName',
  clientSignature: 'agreement.req.clientSignature',
  clientAccepted: 'agreement.req.clientAccepted',
  clientEsignConsent: 'agreement.req.clientEsignConsent',
  clientLiabilityAck: 'agreement.req.clientLiabilityAck',
  inspectorName: 'agreement.req.inspectorName',
  inspectorSignature: 'agreement.req.inspectorSignature',
  inspectorAccepted: 'agreement.req.inspectorAccepted'
};

export function AgreementCard(props: {
  agreement: V2Agreement;
  subject: AgreementSubject;
  onChange: (next: V2Agreement) => void;
  readOnly?: boolean;
  // NY F1 Round 1 (§15.1 effect 3): the ordinary New York Agreement is not EXECUTED or PRINTED while
  // the formal workflow is blocked. Unlike readOnly this deliberately leaves the fee, the Demo
  // toggle and the names editable — those are how an inspector corrects what caused the block.
  signingBlocked?: boolean;
  // Details screen only: share the signed agreement as its own PDF.
  onSharePdf?: () => void;
  sharing?: boolean;
  // Inside another card (New Inspection step 4): no card chrome of its own.
  embedded?: boolean;
  // Details screen only (the inspection exists): signing through a one-time link (Owner items 4 / 45).
  remote?: { inspection: V2Inspection };
  testId?: string;
}) {
  const { t, locale } = useT();
  const [viewOpen, setViewOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [remoteOpen, setRemoteOpen] = useState(false);
  const remoteLink = useStore(remoteSignStore, (s) => (props.remote ? (s.links.find((l) => l.inspectionId === props.remote?.inspection.id) ?? null) : null));
  const { agreement } = props;
  const readOnly = props.readOnly === true;
  const signed = agreementSigned(agreement);
  const missing = missingAgreementRequirements(agreement);
  // Details screen: the PDF made at signing could not be made (iPhone real-photo run 2026-10-26).
  const pdfState = useStore(agreementPdfStore, (s) => s);
  const pdfFailed = props.remote !== undefined && signed && !readOnly && agreementPdfFailed(props.remote.inspection, pdfState);

  // Owner 2026-10-07: a change that re-opens a SIGNED agreement (the fee, Demo, the AI consent) used
  // to do so silently, so the inspector went on believing it was signed. It now asks first; Cancel
  // leaves the signed agreement exactly as it was.
  const [pendingReopen, setPendingReopen] = useState<V2Agreement | null>(null);

  function apply(next: V2Agreement) {
    if (signed && !agreementSigned(next)) setPendingReopen(next);
    else props.onChange(next);
  }

  function changeFee(amount: string) {
    const next = withAgreementFee(agreement, amount);
    apply(signed && next.fee.amount !== agreement.fee.amount ? unsigned(next) : next);
  }

  function changeDemo(demo: boolean) {
    const next = withAgreementDemo(agreement, demo);
    apply(signed && next !== agreement ? unsigned(next) : next);
  }

  // v5: the language the document is printed and signed in. A change re-opens a signed agreement (asked first).
  function changeLanguage(language: AgreementLanguage) {
    apply(withAgreementLanguage(agreement, language));
  }

  // v4 §6: additional services with their cost. A change re-opens a signed agreement (asked first).
  function changeServices(next: V2AdditionalService[]) {
    apply(withAdditionalServices(agreement, next));
  }
  const services = agreement.additionalServices;

  const partyRow = (label: string, party: V2Agreement['client'], testId: string) => {
    const done = partySigned(party);
    return (
      <div className="form-row agreement-party-row" data-testid={testId} data-signed={done ? 'true' : 'false'}>
        <span className="form-row-label">{label}</span>
        <span className={`form-row-value ${done ? 'agreement-party-done' : 'form-row-placeholder'}`}>
          {done ? `${party.name} · ${party.signedAt !== null ? t('agreement.signedOn', { date: formatDate(party.signedAt) }) : t('agreement.signed')}` : t('agreement.notSigned')}
        </span>
      </div>
    );
  };

  return (
    <section className={`agreement-card ${props.embedded ? 'agreement-embedded' : 'card'}`} data-testid={props.testId ?? 'agreement-card'} data-signed={signed ? 'true' : 'false'} data-demo={agreement.demo ? 'true' : 'false'}>
      <div className="card-head">
        <p className="card-title agreement-title">{t('agreement.title')}</p>
        <div className="card-head-right badge-row">
          {agreement.demo ? (
            <span className="badge badge-err" data-testid="agreement-demo-badge">
              {t('report.demoWatermark')}
            </span>
          ) : null}
          <span className={`badge ${signed ? 'badge-ok' : 'badge-warn'}`} data-testid="agreement-status">
            {signed ? t('agreement.signed') : t('agreement.notSigned')}
          </span>
        </div>
      </div>
      <p className="card-note">{t('agreement.fixedNote')}</p>

      <AgreementLanguageRow agreement={agreement} onChange={changeLanguage} disabled={readOnly} testId="agreement-language" />

      <div className="form-rows" data-testid="agreement-fee">
        <RowInput
          id="agreement-fee-amount"
          label={agreement.demo ? t('agreement.feeLabel') : labelWithTag(locale, t('agreement.feeLabel'), t('common.required'))}
          value={agreement.fee.amount}
          onChange={changeFee}
          inputMode="decimal"
          placeholder={t('agreement.feePlaceholder')}
          disabled={readOnly || agreement.demo}
          testId="agreement-fee-amount"
        />
        <p className="form-rows-or">{t('agreement.orDemo')}</p>
        <SwitchRow label={t('agreement.demo')} on={agreement.demo} onToggle={changeDemo} disabled={readOnly} testId="agreement-demo" />
      </div>
      <p className="card-note">{agreement.demo ? t('agreement.demoHint') : t('agreement.paymentOutside')}</p>

      {/* v4 §6: additional services agreed for this property, each with its cost (197-4.2(a)). */}
      <div className="form-rows agreement-services" data-testid="agreement-services">
        {services.map((service, index) => (
          <div className="agreement-service-row" key={index} data-testid={`agreement-service-${index}`}>
            <RowInput
              id={`agreement-service-name-${index}`}
              label={t('agreement.serviceName')}
              value={service.name}
              onChange={(name) => changeServices(services.map((s, i) => (i === index ? { ...s, name } : s)))}
              placeholder={t('agreement.serviceNamePlaceholder')}
              disabled={readOnly}
              testId={`agreement-service-name-${index}`}
            />
            <RowInput
              id={`agreement-service-fee-${index}`}
              label={t('agreement.serviceFee')}
              value={service.fee}
              onChange={(fee) => changeServices(services.map((s, i) => (i === index ? { ...s, fee } : s)))}
              inputMode="decimal"
              placeholder={t('agreement.feePlaceholder')}
              disabled={readOnly}
              testId={`agreement-service-fee-${index}`}
            />
            {!readOnly ? (
              <Button variant="text" size="small" block={false} icon="delete" onClick={() => changeServices(services.filter((_, i) => i !== index))} testId={`agreement-service-remove-${index}`}>
                {t('agreement.serviceRemove')}
              </Button>
            ) : null}
          </div>
        ))}
        {!readOnly ? (
          <Button variant="text" size="small" block={false} icon="add" onClick={() => changeServices([...services, { name: '', fee: '' }])} testId="agreement-service-add">
            {t('agreement.serviceAdd')}
          </Button>
        ) : services.length === 0 ? (
          <p className="card-note">{t('agreement.servicesNone')}</p>
        ) : null}
      </div>

      {/* Copy of 2026-10-19 (Owner Q3): the CLIENT ticks the consent to cloud storage + AI-assisted
          analysis in the signing sheet, with its time. The card only shows where it stands. */}
      <div className="form-rows">
        <div className="form-row agreement-party-row agreement-consent-row" data-testid="agreement-consent" data-consent={agreement.clientAiConsent ? 'true' : 'false'}>
          <span className="form-row-label">{t('agreement.aiConsentRowLabel')}</span>
          <span className={`form-row-value ${agreement.clientAiConsent ? 'agreement-party-done' : 'form-row-placeholder'}`} data-testid="agreement-ai-consent-state">
            {agreement.clientAiConsent ? (agreement.clientAiConsentAt !== null ? t('agreement.aiConsentGivenOn', { date: formatDate(agreement.clientAiConsentAt) }) : t('agreement.aiConsentGiven')) : t('agreement.aiConsentBySigning')}
          </span>
        </div>
      </div>
      <p className="card-note agreement-consent-note">{t('agreement.aiConsentOptional')}</p>

      <div className="form-rows agreement-parties">
        {partyRow(t('agreement.clientSignature'), agreement.client, 'agreement-client-row')}
        {partyRow(t('agreement.inspectorSignature'), agreement.inspector, 'agreement-inspector-row')}
      </div>
      {remoteLink && !signed ? (
        <p className="card-note agreement-remote-state" data-testid="agreement-remote-state">
          {t('remote.cardWaiting', { date: formatDate(remoteLink.expiresAt) })}
        </p>
      ) : null}
      {agreement.clientRemote && partySigned(agreement.client) && !signed ? (
        <p className="card-note agreement-remote-state" data-testid="agreement-remote-done">
          {t('remote.cardClientSigned')}
        </p>
      ) : null}
      {!signed && !readOnly && missing.length > 0 ? (
        <p className="agreement-missing" data-testid="agreement-missing">
          {t('agreement.missing', { list: missing.map((m) => t(REQUIREMENT_KEYS[m])).join(' · ') })}
        </p>
      ) : null}

      {pdfFailed && props.remote ? (
        <Banner
          tone="warning"
          icon="warning"
          title={t('report.agreementFailed')}
          body={t('agreement.pdfKeptFailed')}
          action={
            <Button variant="text" size="small" block={false} icon="refresh" onClick={() => retryAgreementPdf(props.remote!.inspection.id)} testId="agreement-pdf-retry">
              {t('common.retry')}
            </Button>
          }
          testId="agreement-pdf-failed"
        />
      ) : null}

      <div className="agreement-actions">
        <div className="btn-pair">
          <Button variant="secondary" size="regular" icon="description" onClick={() => setViewOpen(true)} testId="agreement-view">
            {t('agreement.view')}
          </Button>
          {!readOnly && props.signingBlocked !== true ? (
            <Button variant={signed ? 'secondary' : 'primary'} size="regular" icon="draw" onClick={() => setSignOpen(true)} testId="agreement-sign">
              {signed ? t('agreement.resign') : t('agreement.sign')}
            </Button>
          ) : null}
        </div>
        {props.remote && !signed && !readOnly && props.signingBlocked !== true && !partySigned(agreement.client) ? (
          <Button variant="text" icon="link" onClick={() => setRemoteOpen(true)} testId="agreement-remote">
            {remoteLink ? t('remote.openLink') : t('remote.sendLink')}
          </Button>
        ) : null}
        {props.onSharePdf && signed && props.signingBlocked !== true ? (
          <Button variant="secondary" icon="picture_as_pdf" onClick={props.onSharePdf} disabled={props.sharing} testId="agreement-share-pdf">
            {props.sharing ? t('report.generating') : t('agreement.pdfShare')}
          </Button>
        ) : null}
      </div>

      {pendingReopen ? (
        <ConfirmSheet
          title={t('agreement.reopenTitle')}
          body={t('agreement.reopenBody')}
          confirmLabel={t('agreement.reopenConfirm')}
          onConfirm={() => {
            props.onChange(pendingReopen);
            setPendingReopen(null);
          }}
          onCancel={() => setPendingReopen(null)}
          testId="agreement-reopen-confirm"
        />
      ) : null}
      {viewOpen ? <AgreementViewSheet agreement={agreement} subject={props.subject} onClose={() => setViewOpen(false)} /> : null}
      {remoteOpen && props.remote ? (
        <RemoteSignSheet
          inspection={props.remote.inspection}
          onClose={() => setRemoteOpen(false)}
          onSignNow={() => {
            setRemoteOpen(false);
            setSignOpen(true);
          }}
        />
      ) : null}
      {signOpen ? (
        <AgreementSignSheet
          agreement={agreement}
          subject={props.subject}
          onSave={(next) => {
            props.onChange(next);
            setSignOpen(false);
          }}
          onClose={() => setSignOpen(false)}
        />
      ) : null}
    </section>
  );
}
